// Staff usage analytics at /agency-management/usage: how customers use the
// product, for all customers, one department, or one officer (the scope lives in
// the URL: ?agency=<id>&officer=<id>&range=30d). Reuses the portal's login.
import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import Button from "../../common/Button";
import StatTile from "../../common/StatTile";
import { Reveal } from "../../common/Reveal";
import { installStaffRequestHeader } from "../../../utilities/staffRequestHeader";
import StaffHeader, { STAFF_CONTAINER } from "../StaffHeader";
import CategorySection from "./CategorySection";
import DateRangeControl from "./DateRangeControl";
import EntityTable from "./EntityTable";
import { ActivityFeed, AgencyHoursCard, HoursSavedCard } from "./OfficerPanel";
import TrendChart, { ALL_ACTIONS } from "./TrendChart";
import {
  AgencyTimeSaved,
  CatalogResponse,
  EntityRow,
  formatCount,
  formatHours,
  pctDelta,
  RangePreset,
  resolvePreset,
  TimeSaved,
  UsageResponse,
} from "./usageTypes";

const ADMIN_EMAIL_KEY = "agency-management-email";
const ADMIN_AUTH_KEY = "agency-management-auth";
const API = "/api/agency-management/usage";

type TimeSavedPayload = { rows?: Record<string, TimeSaved>; total?: AgencyTimeSaved; officer?: TimeSaved | null; typing?: UsageResponse["typing"] };

export default function UsageDashboard() {
  const email = localStorage.getItem(ADMIN_EMAIL_KEY) || "";
  const authed = localStorage.getItem(ADMIN_AUTH_KEY) === "true" && !!email;
  if (!authed) return <Navigate to="/agency-management" replace />;
  return <Dashboard adminEmail={email} />;
}

function Dashboard({ adminEmail }: { adminEmail: string }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const agencyId = params.get("agency");
  const officerId = agencyId ? params.get("officer") : null;
  const preset = (params.get("range") as RangePreset) || "30d";
  const range = useMemo(
    () => resolvePreset(preset, { from: params.get("from") || undefined, to: params.get("to") || undefined }),
    [preset, params]
  );

  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [data, setData] = useState<UsageResponse | null>(null);
  const [timeSaved, setTimeSaved] = useState<TimeSavedPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [chartKey, setChartKey] = useState("cases_created");
  const [hideZeros, setHideZeros] = useState(false);

  const level = officerId ? "officer" : agencyId ? "agency" : "overview";
  const path = officerId
    ? `${API}/agencies/${agencyId}/officers/${officerId}`
    : agencyId
      ? `${API}/agencies/${agencyId}`
      : `${API}/overview`;

  const logout = useCallback(() => {
    localStorage.removeItem(ADMIN_AUTH_KEY);
    navigate("/agency-management");
  }, [navigate]);

  useEffect(() => installStaffRequestHeader(adminEmail), [adminEmail]);

  // Opening a department or officer starts at the top, not wherever the row was.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [agencyId, officerId]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get<CatalogResponse>(`${API}/metrics`)
      .then((r) => !cancelled && setCatalog(r.data))
      .catch((e) => e?.response?.status === 401 && logout());
    return () => {
      cancelled = true;
    };
  }, [logout]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    axios
      .get<UsageResponse>(path, { params: { from: range.from, to: range.to } })
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.status === 401) return logout();
        setError(e?.response?.data?.error || "Could not load usage.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [path, range.from, range.to, logout]);

  // Hours saved reads every case in the window, so it loads on its own and the
  // rest of the page never waits on it.
  useEffect(() => {
    setTimeSaved(null);
    if (!agencyId) return;
    let cancelled = false;
    axios
      .get<TimeSavedPayload>(`${API}/agencies/${agencyId}/time-saved`, { params: { from: range.from, to: range.to, officer: officerId || undefined } })
      .then((r) => !cancelled && setTimeSaved(r.data))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [agencyId, officerId, range.from, range.to]);

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(changes)) (v === null ? next.delete(k) : next.set(k, v));
    setParams(next);
  };

  const setPreset = (p: RangePreset) => {
    if (p === "custom") update({ range: "custom", from: range.from, to: range.to });
    else update({ range: p === "30d" ? null : p, from: null, to: null });
  };

  const metrics = useMemo(() => catalog?.metrics ?? [], [catalog]);
  const accessKeys = useMemo(() => new Set(metrics.filter((m) => m.category === "access").map((m) => m.key)), [metrics]);
  const allKeys = useMemo(() => metrics.filter((m) => !accessKeys.has(m.key)).map((m) => m.key), [metrics, accessKeys]);
  const chartLabel = metrics.find((m) => m.key === chartKey)?.label ?? chartKey;
  const summary = data?.summary;

  const tile = (key: string, label?: string) => {
    const t = summary?.totals[key];
    const meta = metrics.find((m) => m.key === key);
    return (
      <StatTile
        key={key}
        label={label || meta?.label || key}
        value={t ? formatCount(t.current) : "-"}
        delta={t ? pctDelta(t.current, t.previous) : null}
        hint={t && t.users > 0 ? `${t.users} ${t.users === 1 ? "officer" : "officers"}` : undefined}
        selected={chartKey === key}
        onClick={() => setChartKey(key)}
      />
    );
  };

  const hoursTotal: { personalHours: number; aiCases: number } | null | undefined =
    level === "agency" ? timeSaved?.total : level === "officer" ? timeSaved?.officer : null;
  const rowsWithHours: EntityRow[] = (data?.rows ?? []).map((r) => (timeSaved?.rows?.[r.id] ? { ...r, timeSaved: timeSaved.rows[r.id] } : r));

  return (
    <div className="min-h-screen bg-base-200">
      <StaffHeader onLogout={logout} />

      <main className={`${STAFF_CONTAINER} py-6 space-y-6`}>
        <Reveal className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <nav aria-label="Scope" className="flex items-center gap-1 text-sm text-base-content/60 mb-1">
              <button type="button" className={cn("hover:text-base-content", level === "overview" && "font-semibold text-base-content")} onClick={() => update({ agency: null, officer: null })}>
                All customers
              </button>
              {data?.agency && (
                <>
                  <ChevronRightIcon className="size-3" />
                  <button type="button" className={cn("hover:text-base-content", level === "agency" && "font-semibold text-base-content")} onClick={() => update({ officer: null })}>
                    {data.agency.name}
                  </button>
                </>
              )}
              {data?.officer && (
                <>
                  <ChevronRightIcon className="size-3" />
                  <span className="font-semibold text-base-content">{data.officer.name}</span>
                </>
              )}
            </nav>
            <h1 className="m-0 text-3xl font-semibold text-base-content">
              {level === "officer" ? data?.officer?.name || "Officer" : level === "agency" ? data?.agency?.name || "Department" : "How customers use the product"}
            </h1>
            {data?.officer && <p className="m-0 mt-1 text-sm text-base-content/60">{data.officer.email}</p>}
          </div>
          <DateRangeControl preset={preset} from={range.from} to={range.to} onPreset={setPreset} onCustom={(f, t) => update({ range: "custom", from: f, to: t })} />
        </Reveal>

        {error && (
          <div role="alert" className="rounded-xl border border-red-300 dark:border-red-900 bg-red-50 dark:bg-red-950/40 p-4 text-sm text-red-600 dark:text-red-400 flex items-center justify-between">
            {error}
            <Button variant="danger" size="sm" onClick={() => update({})}>
              Retry
            </Button>
          </div>
        )}

        {loading && !data && (
          <div className="flex justify-center py-24">
            <span className="loading loading-spinner loading-lg" />
          </div>
        )}

        {data && summary && catalog && (
          <div className={cn("space-y-6 transition-opacity", loading && "opacity-50")}>
            <Reveal index={1} className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
              <StatTile
                label="Active officers"
                value={formatCount(summary.activeUsers.current)}
                delta={pctDelta(summary.activeUsers.current, summary.activeUsers.previous)}
                hint="did anything in this range"
              />
              {tile("cases_created")}
              {tile("suggestions_insert", "Address suggestions")}
              {tile("forms_filled")}
              {tile("logins")}
              {level === "overview" ? (
                tile("investigation_chat")
              ) : (
                <StatTile
                  label="Hours saved"
                  value={hoursTotal ? formatHours(hoursTotal.personalHours) : "..."}
                  hint={hoursTotal ? `${hoursTotal.aiCases} AI-assisted reports` : "calculating"}
                />
              )}
            </Reveal>

            <Reveal index={2} className="rounded-2xl border border-base-300 bg-base-100 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <p className="m-0 text-xs text-base-content/50">Click any tile to chart it.</p>
                <Button variant={chartKey === ALL_ACTIONS ? "primary" : "secondary"} size="xs" onClick={() => setChartKey(ALL_ACTIONS)}>
                  All actions
                </Button>
              </div>
              <TrendChart summary={summary} range={data.range} metricKey={chartKey} metricLabel={chartLabel} allKeys={allKeys} />
            </Reveal>

            {level === "agency" && (
              <Reveal index={3}>
                <h2 className="m-0 mb-1 text-lg font-semibold text-base-content">Time saved</h2>
                <AgencyHoursCard total={timeSaved?.total} />
                <p className="m-0 mt-2 text-xs text-base-content/50">
                  Each officer's hours use their own typing speed once about {catalog.typing.minSamples} bursts are measured, scaled by {catalog.typing.compositionFactor} to match the report-writing
                  model, and {catalog.typing.defaultWpm} WPM until then.
                </p>
              </Reveal>
            )}

            {level === "officer" && (
              <Reveal index={3} className="space-y-3">
                <h2 className="m-0 text-lg font-semibold text-base-content">Time saved and typing speed</h2>
                <HoursSavedCard timeSaved={timeSaved?.officer ?? null} typing={timeSaved?.typing ?? data.typing ?? null} />
                <h2 className="m-0 pt-2 text-lg font-semibold text-base-content">Recent activity</h2>
                <ActivityFeed feed={data.feed ?? []} />
              </Reveal>
            )}

            {level !== "officer" && data.rows && (
              <Reveal index={4}>
                <h2 className="m-0 mb-2 text-lg font-semibold text-base-content">{level === "overview" ? "Departments" : "Officers"}</h2>
                <EntityTable
                  kind={level === "overview" ? "agency" : "officer"}
                  rows={rowsWithHours}
                  metrics={metrics}
                  showHours={level === "agency"}
                  onOpen={(r) => update(level === "overview" ? { agency: r.id } : { officer: r.id })}
                />
              </Reveal>
            )}

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="m-0 text-lg font-semibold text-base-content">Every metric</h2>
                <label className="flex items-center gap-2 text-sm text-base-content/70 cursor-pointer">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm [--chkbg:theme(colors.blue.500)] [--chkfg:white] checked:border-blue-500"
                    checked={hideZeros}
                    onChange={(e) => setHideZeros(e.target.checked)}
                  />
                  Hide metrics with no activity
                </label>
              </div>
              {catalog.categories.map((c, i) => (
                <CategorySection
                  key={c.key}
                  index={i}
                  label={c.label}
                  metrics={metrics.filter((m) => m.category === c.key)}
                  summary={summary}
                  selectedKey={chartKey}
                  onSelect={(k) => {
                    setChartKey(k);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  hideZeros={hideZeros}
                  trackedSinceLabel={catalog.trackingStartedAt ? new Date(`${catalog.trackingStartedAt}T00:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" }) : null}
                />
              ))}
              <p className="m-0 text-xs text-base-content/50">
                Metrics marked "new" are counted from the day tracking shipped; earlier activity is not in these numbers. Everything else comes from existing records, so it reaches back as far as the data does. Departments are
                attributed by each officer's current agency.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
